import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { VenueTypeCode, VenueType } from '../../landing/constants/venueTypes';
import { VenueTypePattern } from '../../../components/ui/illustrations/venue-themed/patterns/VenueTypePattern';
import { GameController } from '../../../components/ui/illustrations/venue-themed/atoms/gaming/GameController';
import { PoolBall } from '../../../components/ui/illustrations/venue-themed/atoms/snooker/PoolBall';
import { Balloon } from '../../../components/ui/illustrations/venue-themed/atoms/kids/Balloon';

interface VenueDetailThemeProps {
  venueType?: VenueTypeCode | string;
  children: React.ReactNode;
}

/**
 * Returns a venue-type icon at small size for breadcrumbs/badges.
 * Maps each VenueType to its corresponding themed SVG atom.
 */
export function VenueTypeIcon({ venueType, size = 20 }: { venueType?: string; size?: number }) {
  switch (venueType) {
    case VenueType.GAMING:
      return <GameController size={size} />;
    case VenueType.SNOOKER:
      return <PoolBall size={size} />;
    case VenueType.KIDS_PLAYAREA:
      return <Balloon size={size} />;
    default:
      return null;
  }
}

/**
 * VenueDetailTheme - Wrapper component that injects venue-type-specific
 * visual theming (background pattern, color wash) without modifying the
 * VenueDetailPage monolith.
 *
 * When venueType is unknown or undefined, renders children unchanged.
 * Theme values are derived from theme.palette.venue (mode-aware).
 */
export const VenueDetailTheme = React.memo(function VenueDetailTheme({
  venueType,
  children,
}: VenueDetailThemeProps) {
  const theme = useTheme();

  const themeConfig = useMemo(() => {
    // Get venue color from theme palette (mode-aware)
    const getColor = (): string | undefined => {
      switch (venueType) {
        case VenueType.GAMING:
          return theme.palette.venue.gaming;
        case VenueType.SNOOKER:
          return theme.palette.venue.snooker;
        case VenueType.KIDS_PLAYAREA:
          return theme.palette.venue.kids;
        default:
          return undefined;
      }
    };

    const color = getColor();
    if (!color) return null;

    return {
      color,
      // Header gradient overlay (venue color at 8% opacity)
      headerGradient: `linear-gradient(180deg, transparent 60%, ${color}14 100%)`,
      // Section divider gradient line
      dividerGradient: `linear-gradient(90deg, transparent, ${color}40, transparent)`,
      // Sidebar accent border
      sidebarBorderColor: `${color}60`,
    };
  }, [venueType, theme.palette.venue]);

  // If no venue type or unknown type, render children without theming
  if (!themeConfig) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        // Apply venue-color gradient wash across the full overlay
        background: themeConfig.headerGradient,
      }}
    >
      {/* Subtle background pattern at 3% opacity */}
      {venueType && <VenueTypePattern venueType={venueType as VenueTypeCode} opacity={0.03} />}
      {children}
    </Box>
  );
});
